/**
 * Stable Story chapter DOM ids / URL fragments (`#story-event-…`).
 * SSR-safe: no `window`, no randomness — same chapter identity → same anchor.
 */

/** Prefix for chapter regions and hash links (stable contract). */
export const STORY_EVENT_SECTION_ID_PREFIX = "story-event-" as const

const MAX_ANCHOR_BODY_LEN = 200

/** Safe unencoded fragment segment: letters, digits, hyphen, underscore. */
export function isStoryAnchorBodySafe(segment: string): boolean {
  return /^[a-zA-Z0-9_-]+$/.test(segment) && segment.length <= MAX_ANCHOR_BODY_LEN
}

/**
 * When `event.id` is a safe token, returns it; otherwise `null` (caller uses hash fallback).
 */
export function sanitizeEventIdForStoryAnchor(eventId: string): string | null {
  const t = eventId.trim()
  if (!t || t.length > MAX_ANCHOR_BODY_LEN) return null
  if (!isStoryAnchorBodySafe(t)) return null
  return t
}

/** Deterministic 16-hex digest for identity basis (no `crypto`; works client + server). */
export function storyChapterIdentityDigestHex(basis: string): string {
  let h1 = 5381 >>> 0
  let h2 = 52711 >>> 0
  for (let i = 0; i < basis.length; i++) {
    const c = basis.charCodeAt(i)
    h1 = (Math.imul(33, h1) + c) >>> 0
    h2 = (Math.imul(33, h2) + c) >>> 0
  }
  return `${h1.toString(16).padStart(8, "0")}${h2.toString(16).padStart(8, "0")}`
}

export type StoryChapterAnchorIdentityInput = {
  id: string
  date: string
  createdAt: Date
  seriesId: string | null
  title: string
  /** Zero-based position after narrative sort (disambiguates collisions). */
  narrativeIndex: number
}

export function storyChapterIdentityBasis(
  ev: StoryChapterAnchorIdentityInput,
): string {
  const created =
    ev.createdAt instanceof Date
      ? ev.createdAt.toISOString()
      : String(ev.createdAt)
  return [
    ev.id,
    ev.date,
    created,
    String(ev.narrativeIndex),
    ev.seriesId ?? "",
    ev.title,
  ].join("\u001f")
}

/**
 * Preferred section DOM id for one chapter (may collide until
 * {@link assignStableStoryChapterSectionDomIds} runs).
 */
export function storyChapterSectionDomIdCandidate(
  ev: StoryChapterAnchorIdentityInput,
): string {
  const safe = sanitizeEventIdForStoryAnchor(ev.id)
  if (safe) return `${STORY_EVENT_SECTION_ID_PREFIX}${safe}`
  const digest = storyChapterIdentityDigestHex(storyChapterIdentityBasis(ev))
  return `${STORY_EVENT_SECTION_ID_PREFIX}h-${digest}`
}

/** Row shape that carries stable fields for anchor generation (`narrativeIndex` is supplied by the caller). */
export type StoryChapterAnchorAttachable = Omit<
  StoryChapterAnchorIdentityInput,
  "narrativeIndex"
> & {
  chapterSectionDomId?: string
}

/**
 * Assigns unique `chapterSectionDomId` per event in narrative order; resolves
 * sanitize/hash collisions with deterministic `--n` suffixes.
 */
export function assignStableStoryChapterSectionDomIds<
  T extends StoryChapterAnchorAttachable,
>(events: readonly T[]): (T & { chapterSectionDomId: string })[] {
  const used = new Set<string>()
  const out: (T & { chapterSectionDomId: string })[] = []

  for (let i = 0; i < events.length; i++) {
    const ev = events[i]!
    const input: StoryChapterAnchorIdentityInput = {
      id: ev.id,
      date: ev.date,
      createdAt: ev.createdAt,
      seriesId: ev.seriesId,
      title: ev.title,
      narrativeIndex: i,
    }
    let base = storyChapterSectionDomIdCandidate(input)
    let n = 0
    while (used.has(base)) {
      n += 1
      base = `${storyChapterSectionDomIdCandidate(input)}--${n}`
    }
    used.add(base)
    out.push({ ...ev, chapterSectionDomId: base })
  }

  return out
}

/**
 * Client / link helper when only `eventId` is known (e.g. deep link from timeline).
 * Matches server anchors when `eventId` is the same safe token used in Story composition.
 */
export function storyChapterSectionDomIdFromEventIdOnly(eventId: string): string {
  const safe = sanitizeEventIdForStoryAnchor(eventId)
  if (safe) return `${STORY_EVENT_SECTION_ID_PREFIX}${safe}`
  const digest = storyChapterIdentityDigestHex(`solo:\u001f${eventId.trim()}`)
  return `${STORY_EVENT_SECTION_ID_PREFIX}h-${digest}`
}
